import { Subscription } from "rxjs/internal/Subscription"
import { PartialObserver } from "rxjs/internal/types"
import { webSocket, WebSocketSubject } from "rxjs/webSocket"
import { manager } from "./protos/eb.manager"
import { Subject } from "rxjs"
import { loginfo, logdebug } from "./utils"
const modName = "manager-connector"
// IEBMessage is the interface which represents sending and receiving of messages
interface IEBMessage<T> {
  next(msg: T): any
  subscribe(observer?: PartialObserver<T>): Subscription
}
interface IEBManagerConnector extends IEBMessage<manager.Message> {
  notifyStateChanges(observer?: PartialObserver<"con" | "dis">): Subscription
  setID(id: string): IEBManagerConnector
  getID(): string
  close(): void
}
class ManagerConnector implements IEBManagerConnector {

  public static session(): ManagerConnector {
    return new ManagerConnector().reconnectIn(2000)
  }

  // session id or peer id
  private id: string
  private roomURL: string
  private url: string
  private reconnectInterval: number
  private conn: WebSocketSubject<manager.Message>
  private messageSubject: Subject<manager.Message> = new Subject<manager.Message>()
  private rootSubscription: Subscription
  private stateChannel: Subject<"con" | "dis"> = new Subject<"con" | "dis">()

  // every time assign reset the id
  public assign = (url: string): ManagerConnector => {
    this.roomURL = url
    this.url = url
    return this
  }

  public reconnectIn = (ms: number): ManagerConnector => {
    this.reconnectInterval = ms
    return this
  }

  public start(): ManagerConnector {
    if (this.conn) {
      this.conn.complete()
      this.conn = null
    }
    this.conn = webSocket({
      url: this.url,
      protocol: "easybroadcast.fr_2.0",
      binaryType: "arraybuffer",
      deserializer: (event: MessageEvent) => {
        try {
          const message = manager.Message.decode(new Uint8Array(event.data))
          return message
        } catch (error) {
          console.warn(error)
          return null
        }
      },
      serializer: (msg: manager.Message) => {
        return manager.Message.encode(msg).finish()
      },
      openObserver: {next: () => {this.stateChannel.next("con")}},
      closeObserver: {next: () => {this.stateChannel.next("dis")}},
    })
    if (this.rootSubscription) {
      this.rootSubscription.unsubscribe()
    }
    this.rootSubscription = this.conn.subscribe(
      (msg) => this.messageSubject.next(msg),
      (err) => {
        console.info(err)
        const toid = setTimeout(() => {
          this.start()
          clearTimeout(toid)
        }, this.reconnectInterval)
      },
      () => {
        console.info("websocket complet")
      },
    )
    return this
  }

  public close = (): void => {
    if (this.conn) {
      this.conn.complete()
    }
  }

  public next = (msg: manager.Message) => {
    if (!this.conn.isStopped) {
      this.conn.next(msg)
    } else {
      console.warn("connection is stopped, message not sent")
    }
  }

  // set ID and set the url to include the peer id
  public setID = (id: string): ManagerConnector => {
    this.id = id
    this.url = this.roomURL + "/" + this.id
    return this
  }

  public notifyStateChanges = (observer?: PartialObserver<"con" | "dis">): Subscription => {
    return this.stateChannel.subscribe(observer)
  }

  public getID = (): string => {
    return this.id
  }

  // reset the id and exclude the peer id from url
  public noID = (): ManagerConnector => {
    this.id = ""
    this.url = this.roomURL
    return this
  }

  public subscribe = (observer?: PartialObserver<manager.Message>): Subscription => {
    return this.messageSubject.subscribe(observer)
  }

}

export { ManagerConnector, IEBMessage, IEBManagerConnector }
